import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../../../app/store'

export interface FormState {
  historyExamination: Record<string, any>
  vitals: Record<string, any>
  systemicExamination: Record<string, any>
  clinicalExamination: Record<string, any>
  examination: Record<string, any>
  diagnosis: Record<string, any>
  treatmentPlan: Record<string, any>
  medications: Record<string, any>
  objectivePhysio: Record<string, any>
  painHistoryPhysio: Record<string, any>
  lastUpdated: string | null
  isLoading: boolean
  isInitialLoad: boolean
  isHistoryOpdFormSubmit: boolean
  isVitalsOpdFormSubmit: boolean
  isSystemicExamOpdFormSubmit: boolean
  isDiagnosisOpdFormSubmit: boolean
  isTreatmentPlanOpdFormSubmit: boolean
  isPainHistoryOpdFormSubmit: boolean
  isObjectiveOpdFormSubmit: boolean
}

const initialState: FormState = {
  historyExamination: {},
  vitals: {},
  systemicExamination: {},
  clinicalExamination: {},
  examination: {},
  diagnosis: {},
  treatmentPlan: {},
  medications: {},
  objectivePhysio: {},
  painHistoryPhysio: {},
  lastUpdated: null,
  isLoading: false,
  isInitialLoad: true,
  isHistoryOpdFormSubmit: false,
  isVitalsOpdFormSubmit: false,
  isSystemicExamOpdFormSubmit: false,
  isDiagnosisOpdFormSubmit: false,
  isTreatmentPlanOpdFormSubmit: false,
  isPainHistoryOpdFormSubmit: false,
  isObjectiveOpdFormSubmit: false,
}

const opdFormsReducer = createSlice({
  name: 'form',
  initialState,
  reducers: {
    updateHistoryExamination: (
      state,
      action: PayloadAction<Record<string, any>>
    ) => {
      state.historyExamination = {
        ...state?.historyExamination,
        ...action?.payload,
      }
      state.lastUpdated = new Date()?.toISOString()
    },

    updateVitals: (state, action: PayloadAction<Record<string, any>>) => {
      state.vitals = { ...state?.vitals, ...action?.payload }

      state.lastUpdated = new Date()?.toISOString()
    },

    updateSystemicExamination: (
      state,
      action: PayloadAction<Record<string, any>>
    ) => {
      state.systemicExamination = {
        ...state?.systemicExamination,
        ...action?.payload,
      }

      state.lastUpdated = new Date()?.toISOString()
    },

    updateDiagnosis: (state, action: PayloadAction<Record<string, any>>) => {
      state.diagnosis = { ...state?.diagnosis, ...action?.payload }

      state.lastUpdated = new Date()?.toISOString()
    },

    updateTreatmentPlan: (
      state,
      action: PayloadAction<Record<string, any>>
    ) => {
      state.treatmentPlan = { ...state?.treatmentPlan, ...action?.payload }

      state.lastUpdated = new Date()?.toISOString()
    },

    updateMedications: (state, action: PayloadAction<Record<string, any>>) => {
      state.medications = { ...state?.medications, ...action?.payload }

      state.lastUpdated = new Date()?.toISOString()
    },

    updateObjectivePhysio: (
      state,
      action: PayloadAction<Record<string, any>>
    ) => {
      state.objectivePhysio = { ...state?.objectivePhysio, ...action?.payload }

      state.lastUpdated = new Date()?.toISOString()
    },

    updatePainHistoryPhysio: (
      state,
      action: PayloadAction<Record<string, any>>
    ) => {
      state.painHistoryPhysio = {
        ...state?.painHistoryPhysio,
        ...action?.payload,
      }

      state.lastUpdated = new Date()?.toISOString()
    },

    updateClinicalExamination: (
      state,
      action: PayloadAction<Record<string, any>>
    ) => {
      state.clinicalExamination = {
        ...state?.clinicalExamination,
        ...action?.payload,
      }

      state.lastUpdated = new Date()?.toISOString()
    },

    updateExamination: (state, action: PayloadAction<Record<string, any>>) => {
      state.examination = { ...state?.examination, ...action?.payload }

      state.lastUpdated = new Date()?.toISOString()
    },

    resetAllForms: () => initialState,

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action?.payload
    },
    setIsInitialLoad: (state, action: PayloadAction<boolean>) => {
      state.isInitialLoad = action?.payload
    },
    setIsHistoryFormSubmit: (state, action: PayloadAction<boolean>) => {
      state.isHistoryOpdFormSubmit = action?.payload
    },
    setIsVitalsFormSubmit: (state, action: PayloadAction<boolean>) => {
      state.isVitalsOpdFormSubmit = action?.payload
    },
    setIsSystemicExamOpdFormSubmit: (state, action: PayloadAction<boolean>) => {
      state.isSystemicExamOpdFormSubmit = action?.payload
    },
    setIsDiagnosisOpdFormSubmit: (state, action: PayloadAction<boolean>) => {
      state.isDiagnosisOpdFormSubmit = action?.payload
    },
    setIsTreatmentPlanOpdFormSubmit: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isTreatmentPlanOpdFormSubmit = action?.payload
    },
    setIsPainHistoryOpdFormSubmit: (state, action: PayloadAction<boolean>) => {
      state.isPainHistoryOpdFormSubmit = action?.payload
    },
    setIsObjectiveOpdFormSubmit: (state, action: PayloadAction<boolean>) => {
      state.isObjectiveOpdFormSubmit = action?.payload
    },
  },
})

export const {
  updateHistoryExamination,
  updateVitals,
  updateSystemicExamination,
  updateClinicalExamination,
  updateExamination,
  updateDiagnosis,
  updateTreatmentPlan,
  updateMedications,
  updateObjectivePhysio,
  updatePainHistoryPhysio,
  setIsInitialLoad,
  resetAllForms,
  setLoading,
  setIsHistoryFormSubmit,
  setIsVitalsFormSubmit,
  setIsSystemicExamOpdFormSubmit,
  setIsDiagnosisOpdFormSubmit,
  setIsTreatmentPlanOpdFormSubmit,
  setIsPainHistoryOpdFormSubmit,
  setIsObjectiveOpdFormSubmit,
} = opdFormsReducer.actions

export default opdFormsReducer.reducer

export const selectHistoryExamination = (state: RootState) =>
  state?.opdFormsSlice?.historyExamination

export const selectVitals = (state: RootState) => state?.opdFormsSlice?.vitals

export const selectSystemicExamination = (state: RootState) =>
  state?.opdFormsSlice?.systemicExamination

export const selectClinicalExamination = (state: RootState) =>
  state?.opdFormsSlice?.clinicalExamination

export const selectExamination = (state: RootState) =>
  state?.opdFormsSlice?.examination

export const selectDiagnosis = (state: RootState) =>
  state?.opdFormsSlice?.diagnosis

export const selectTreatmentPlan = (state: RootState) =>
  state?.opdFormsSlice?.treatmentPlan

export const selectMedications = (state: RootState) =>
  state?.opdFormsSlice?.medications

export const selectObjectivePhysio = (state: RootState) =>
  state?.opdFormsSlice?.objectivePhysio

export const selectPainHistoryPhysio = (state: RootState) =>
  state?.opdFormsSlice?.painHistoryPhysio

export const selectIsLoading = (state: RootState) =>
  state?.opdFormsSlice?.isLoading

export const selectLastUpdated = (state: RootState) =>
  state?.opdFormsSlice?.lastUpdated

export const isInitialLoadForOPDForms = (state: RootState) =>
  state?.opdFormsSlice?.isInitialLoad

export const isHistoryFormSubmitted = (state: RootState) =>
  state?.opdFormsSlice?.isHistoryOpdFormSubmit
export const isVitalsFormSubmitted = (state: RootState) =>
  state?.opdFormsSlice?.isVitalsOpdFormSubmit
export const isSystemicExamOpdFormSubmitted = (state: RootState) =>
  state?.opdFormsSlice?.isSystemicExamOpdFormSubmit
export const isDiagnosisOpdFormSubmitted = (state: RootState) =>
  state?.opdFormsSlice?.isDiagnosisOpdFormSubmit
export const isTreatmentPlanOpdFormSubmitted = (state: RootState) =>
  state?.opdFormsSlice?.isTreatmentPlanOpdFormSubmit
export const isPainHistoryOpdFormSubmitted = (state: RootState) =>
  state?.opdFormsSlice?.isPainHistoryOpdFormSubmit
export const isObjectiveOpdFormSubmitted = (state: RootState) =>
  state?.opdFormsSlice?.isObjectiveOpdFormSubmit
